/**
 * The shape of a member's weekly activity streak, and the pure function that
 * turns it into seven renderable days.
 *
 * ## Why these types are here and not in `@sukha/db`
 *
 * They are hand-written object types, not `InferSelectModel` off a Drizzle
 * table. AGENTS.md draws the line exactly there: import a type from
 * `@sukha/shared` unless it is derived from a table. `queries/stats.ts` builds
 * these by hand from an aggregate query, so nothing about them is tied to the
 * schema.
 *
 * ## Why `processWeeklyStreakData` moved (TX-4865)
 *
 * It has three callers that cannot share a home any other way: the chart router
 * moving to `apps/api`, the Morning After email in `apps/web`, and
 * `WeeklyStreakCard` -- a **Client Component**. That last one is the constraint.
 * The function is pure, but it lived in `apps/web/src/lib/utils/` importing its
 * types from `@sukha/db`; putting it in `@sukha/db` so `apps/api` could reach it
 * would have made a client component import the database package at runtime,
 * which `server-only-package-boundary.guard.test.ts` refuses -- correctly.
 *
 * `@sukha/shared` is the one place all three can import from: no `drizzle-orm`,
 * no `@sukha/db`, nothing that must not reach a browser.
 */
import { addDays } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";

import { calculateWeekDates } from "./streak.js";

/** One day of the streak window, as `getUserActivityStreak` assembles it. */
export type DailyActivityStreak = {
  date: string; // Format: YYYY-MM-DD
  sessionCount: number | null;
  totalDuration: string | null; // Total session duration in seconds
  streakCount?: number | null; // Streak count from user_streaks table
  longestStreak?: boolean; // Longest streak flag from user_streaks table
};

/** A member's streak window plus the two headline numbers. */
export type ActivityStreakData = {
  days: DailyActivityStreak[];
  longestStreak: number;
  currentStreak: number;
};

export interface ProcessedStreakDay {
  date: string; // YYYY-MM-DD
  worked: boolean; // Has a streak record
  isToday: boolean;
  streakNumber: number | null; // Streak count from user_streaks table
  longestStreak: boolean; // Longest streak flag
  weekday: string; // Short weekday name (e.g., "Mon")
  dateLabel: string; // Formatted date (e.g., "Jan 15")
  index: number; // 0-6 for Sunday-Saturday
}

/**
 * Process weekly streak data into a standardized format
 * Used by both React component and email chart rendering
 * 
 * @param streakData - Raw streak data from getUserActivityStreak
 * @param userTimezone - User's timezone for date calculations
 * @returns Array of processed day data for the week (Sunday-Saturday)
 */
export function processWeeklyStreakData(
  streakData: ActivityStreakData | null | undefined,
  userTimezone: string
): ProcessedStreakDay[] {
  if (!streakData?.days) {
    return [];
  }

  // Calculate the Sunday-Saturday week containing today
  const { sundayDate, todayStr } = calculateWeekDates(undefined, userTimezone);

  // Build a map of dates to streak records
  const streakRecordMap = new Map<string, { streakCount: number; longestStreak: boolean }>();
  streakData.days.forEach((day: DailyActivityStreak) => {
    const dateStr = day.date; // Always a string in YYYY-MM-DD format
    if (day.streakCount !== null && day.streakCount !== undefined) {
      streakRecordMap.set(dateStr, {
        streakCount: day.streakCount,
        longestStreak: day.longestStreak ?? false,
      });
    }
  });

  // Build array for the week (Sunday through Saturday)
  const weekDays: ProcessedStreakDay[] = [];

  for (let i = 0; i < 7; i++) {
    // Calculate date for this day in the week
    // sundayDate is already in the user's timezone (from calculateWeekDates)
    // We add days to it, then format in the user's timezone
    const dayInUserTz = addDays(sundayDate, i);
    const dateStr = formatInTimeZone(dayInUserTz, userTimezone, 'yyyy-MM-dd');
    const isToday = dateStr === todayStr;
    const record = streakRecordMap.get(dateStr);

    // Format weekday and date label in user's timezone
    const weekday = formatInTimeZone(dayInUserTz, userTimezone, 'EEE');
    const dateLabel = formatInTimeZone(dayInUserTz, userTimezone, 'MMM d');

    weekDays.push({
      date: dateStr,
      worked: record !== undefined, // A day is "worked" if there's a streak record for it
      isToday,
      streakNumber: record?.streakCount ?? null,
      longestStreak: record?.longestStreak ?? false,
      weekday,
      dateLabel,
      index: i,
    });
  }

  return weekDays;
}

